import { RefObject, useEffect, useState } from "react";

export const useVirtualScroll = <T>(ref: RefObject<HTMLElement>, array: T[], childHeight = 50) => {
	const [slidingWindow, setSlidingWindow] = useState<T[]>([]);
	const [startIndex, setStartIndex] = useState(0);
	const [endIndex, setEndIndex] = useState(0);
	const [paddingTop, setPaddingTop] = useState(0);
	const [paddingBottom, setPaddingBottom] = useState(0);

	useEffect(() => {
		if (!ref.current) {
			return () => {};
		}

		const parentHeight = ref.current.clientHeight;
		const size = Math.ceil(parentHeight / childHeight) * 2;

		const updateState = (newStartIndex: number) => {
			const newEndIndex = Math.min(newStartIndex + size, array.length);
			setSlidingWindow(array.slice(newStartIndex, newEndIndex));
			setPaddingTop(newStartIndex * childHeight);
			setPaddingBottom((array.length - newEndIndex) * childHeight);
			setStartIndex(newStartIndex);
			setEndIndex(newEndIndex);
		};

		const onScroll = () => {
			if (!ref.current) {
				return;
			}
			const scrollTop = ref.current.scrollTop;
			const newStartIndex = Math.floor(scrollTop / childHeight);
			updateState(newStartIndex);
		};

		ref.current.addEventListener("scroll", onScroll);

		const maxScroll = Math.max(0, array.length * childHeight - parentHeight);
		let scrollPosition = ref.current.scrollTop;
		if (scrollPosition > maxScroll) {
			scrollPosition = maxScroll;
		}

		const initialStartIndex = Math.floor(scrollPosition / childHeight);
		updateState(initialStartIndex);

		return () => {
			if (ref.current) {
				ref.current.removeEventListener("scroll", onScroll);
			}
		};
	}, [ref, array, childHeight]);

	return { slidingWindow, startIndex, paddingTop, paddingBottom, endIndex };
};
